import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { HubSpotIcon, MarketoIcon, SalesForceIcon } from "../../../images";
import { ActionType } from "types";
import {
  CRMData,
  CRMListItem,
  IntegrationInfo,
  IntegrationType,
} from "types/apiTypes";
import { useLazyGetCRMListQuery } from "api";
import { useGetCRMUrlMutation } from "api/companyApi";
import { getErrorMessage } from "utils/getErrorMessage";
import { CustomToast } from "../../CustomToast";
import { SyncCRMModal } from "../SyncCRMModal/SyncCRMModal";
import { CRMMethod } from "./CRMMethod";
import { ResyncMarketoBtn } from "../../../marketo/ResyncMarketoBtn";
import { Integration } from "../../../../types/enums";

const methodList = [
  {
    title: "HubSpot",
    integration: "hubspot",
    text: "Automatically import your contacts and accounts from HubSpot CRM.",
    icon: <HubSpotIcon />,
  },
  {
    title: "Salesforce",
    integration: "salesforce",
    text: "Quickly import contacts and accounts from Salesforce CRM.",
    icon: <SalesForceIcon />,
  },
  {
    title: "Marketo",
    integration: "marketo",
    text: "Import contacts and accounts from Marketo.",
    icon: <MarketoIcon />,
  },
];

type UploadMethod = IntegrationType | "";

export const CRMUpdateMethod = ({
  synced,
  setDataSource,
  campaign_id,
  setIsVisible,
  crmIntegration,
  actionType,
}: {
  synced?: UploadMethod;
  setDataSource: (data: CRMData | null) => void;
  campaign_id: string;
  setIsVisible?: (isVisible: boolean) => void;
  crmIntegration?: IntegrationInfo;
  actionType: ActionType;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [openModal, setOpenModal] = useState<UploadMethod>("");
  const [crm, setCrm] = useState<UploadMethod>("");
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>("");
  const [crmList, setCrmList] = useState<CRMListItem[]>([]);

  useEffect(() => {
    setIsVisible && setIsVisible(!!openModal);
  }, [openModal]);

  const [getCRMUrl] = useGetCRMUrlMutation();

  const [getCRMListQuery, { isFetching }] = useLazyGetCRMListQuery();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const crm = urlParams.get("crm");
    const actionType = urlParams.get("actionType");

    if (crm) {
      actionType !== "connect" && setCrm(crm as UploadMethod);
      navigate(pathname, {
        replace: true,
      });
    }
  }, []);

  useEffect(() => {
    if (crm) {
      getCRMListQuery(crm)
        .unwrap()
        .then((data) => {
          setCrmList(data || []);
          setOpenModal(crm);
          setCrm("");
        })
        .catch(async (err) => {
          if (err?.status === 428) {
            getCRMUrl({
              campaign_id,
              action_type: actionType,
              redirect_path: location.pathname,
              crm,
            })
              .unwrap()
              .then((data) => {
                window.location.href = data.url;
              })
              .catch((err) =>
                toast.error(
                  <CustomToast
                    type="error"
                    text={
                      getErrorMessage(err).globalError ||
                      "Something went wrong. Please try again"
                    }
                    title="Connection Failed."
                  />,
                ),
              );
          } else {
            toast.error(
              <CustomToast
                type="error"
                text={
                  getErrorMessage(err).globalError ||
                  "The company data upload failed."
                }
                title="Upload Failed"
              />,
            );
          }
        });
    }
  }, [crm]);

  const setDataSourceHandler = (data: CRMData | null) => {
    setDataSource(data);
    setUploadMethod(data?.crm ?? "");
  };

  return (
    <Stack flexDirection={campaign_id ? "column" : "row"} gap="8px">
      {methodList.map(({ integration, text, icon, title }) => (
        <CRMMethod
          key={integration}
          isUpdate={!!campaign_id}
          isLoading={crm === integration}
          icon={icon}
          title={title}
          btnHandler={() => setCrm(integration as UploadMethod)}
          text={text}
          isSynced={
            synced === integration ||
            uploadMethod === integration ||
            crmIntegration?.integration === integration
          }
          isDisabled={
            !!uploadMethod ||
            (!!crmIntegration && crmIntegration?.integration !== integration)
          }
          isConnected={!!crmIntegration?.is_connected || synced === integration}
          onDisconnect={() =>
            setDataSourceHandler(
              integration === "marketo"
                ? null
                : { list_id: undefined, crm: "" },
            )
          }
          customBtn={
            !(
              crmIntegration?.is_connected &&
              crmIntegration.integration === integration &&
              crmIntegration?.auth_status === "connected"
            ) ? (
              <ResyncMarketoBtn
                isDisabled={
                  !!crmIntegration && crmIntegration.integration !== integration
                }
                marketoStatus={
                  crmIntegration?.integration === integration
                    ? crmIntegration?.auth_status
                    : undefined
                }
                currentEmail={
                  crmIntegration?.integration === integration
                    ? crmIntegration?.auth_client_email || ""
                    : ""
                }
                platform={integration as Integration}
              />
            ) : undefined
          }
        />
      ))}
      {openModal && (
        <SyncCRMModal
          crmList={crmList}
          openModal={openModal}
          onClose={() => setOpenModal("")}
          setDataSource={setDataSourceHandler}
          setOpenModal={setCrm}
          isLoading={isFetching}
        />
      )}
    </Stack>
  );
};
